// ───────── Formatters ─────────

export const formatCurrency = (v, currency = "COP") => {
  const num = Number(v)
  if (!isFinite(num)) return "—"
  return new Intl.NumberFormat("es-CO", {
    style: "currency", currency, maximumFractionDigits: 0,
  }).format(num)
}

export const formatNumber = (v, decimals = 0) => {
  const num = Number(v)
  if (!isFinite(num)) return "—"
  return new Intl.NumberFormat("es-CO", {
    minimumFractionDigits: decimals, maximumFractionDigits: decimals,
  }).format(num)
}

export const formatPercent = (v, decimals = 2) => {
  const num = Number(v)
  if (!isFinite(num)) return "—"
  return `${num.toFixed(decimals)}%`
}

export const formatGrowth = v => {
  const num = Number(v)
  if (!isFinite(num)) return { text: "—", color: "#64748b", icon: "minus" }
  const text = `${num > 0 ? "+" : ""}${num.toFixed(2)}%`
  if (num > 0) return { text, color: "#10b981", icon: "trending-up" }
  if (num < 0) return { text, color: "#ef4444", icon: "trending-down" }
  return { text, color: "#64748b", icon: "minus" }
}

export const formatFecha = iso => {
  if (!iso) return "—"
  try {
    const s = String(iso).substring(0, 10)
    const [y, m, d] = s.split("-")
    if (y && m && d) return `${d}/${m}/${y}`
    return iso
  } catch { return iso }
}

// ───────── Paleta de familias de impuesto ─────────

export const FAMILIA_COLORES = {
  IVA:        { color: "#3b82f6", bg: "#dbeafe", desc: "Impuesto sobre las Ventas" },
  INC:        { color: "#10b981", bg: "#d1fae5", desc: "Impuesto Nacional al Consumo" },
  ICA:        { color: "#f59e0b", bg: "#fef3c7", desc: "Industria y Comercio" },
  RETENCIONES:{ color: "#8b5cf6", bg: "#ede9fe", desc: "Retenciones" },
  OTROS:      { color: "#94a3b8", bg: "#f1f5f9", desc: "Otros impuestos" },
}

export const familiaConfig = nombre => {
  const k = String(nombre || "").toUpperCase()
  return FAMILIA_COLORES[k] || { color: "#64748b", bg: "#f1f5f9", desc: nombre || "—" }
}

// Paleta general para gráficos
export const PALETA = ["#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#06b6d4", "#ec4899", "#84cc16"]
export const colorPara = i => PALETA[i % PALETA.length]

// ───────── Niveles de tasa efectiva ─────────

export const tasaEfectivaConfig = pct => {
  const v = Number(pct) || 0
  if (v >= 15) return { label: "Muy alta",  color: "#dc2626", desc: "Tasa efectiva superior al 15%" }
  if (v >= 10) return { label: "Alta",      color: "#f59e0b", desc: "Tasa efectiva entre 10% y 15%" }
  if (v >= 5)  return { label: "Moderada",  color: "#3b82f6", desc: "Tasa efectiva entre 5% y 10%" }
  if (v > 0)   return { label: "Baja",      color: "#10b981", desc: "Tasa efectiva inferior al 5%" }
  return { label: "Sin gravamen", color: "#64748b", desc: "Sin valor aplicable" }
}

// ───────── Cobertura ─────────

export const coberturaConfig = nivel => {
  switch (String(nivel || "").toLowerCase()) {
    case "completa":   return { label: "Completa",   color: "#065f46", bg: "#d1fae5", icon: "check-circle-outline" }
    case "parcial":    return { label: "Parcial",    color: "#92400e", bg: "#fef3c7", icon: "alert-circle-outline" }
    case "incompleta": return { label: "Incompleta", color: "#991b1b", bg: "#fee2e2", icon: "alert-octagon-outline" }
    default:           return { label: nivel || "—", color: "#475569", bg: "#f1f5f9", icon: "information-outline" }
  }
}

// ───────── Tipos de impuesto DIAN ─────────

export const TIPOS_IMPUESTO = {
  "01": "IVA",
  "02": "IC",
  "03": "ICA",
  "04": "INC",
  "05": "ReteIVA",
  "06": "ReteFuente",
  "07": "ReteICA",
  "20": "FtoHorticultura",
  "21": "Timbre",
  "22": "Bolsas",
  "23": "INCarbono",
  "24": "INCombustibles",
  "25": "Sobretasa Combustibles",
  "26": "Sobretasa Energía",
  "27": "Estampilla",
  "28": "Contribución",
  "30": "ReteRenta Especial",
  "32": "ICUI",
  "33": "INCBolsas",
  "34": "Otro",
  "ZY": "No Causado",
  "ZZ": "No Aplica",
}

// ───────── Rango del mes ─────────

export const rangoDelMes = (year, month) => {
  const yyyy = String(year)
  const mm = String(month).padStart(2, "0")
  const ultimo = new Date(year, month, 0).getDate()
  return {
    fecha_inicio: `${yyyy}-${mm}-01`,
    fecha_fin: `${yyyy}-${mm}-${String(ultimo).padStart(2, "0")}`,
  }
}

// ───────── Export client-side a CSV ─────────

export const descargarCSV = (filename, headers, rows) => {
  const escape = v => {
    if (v == null) return ""
    const s = String(v)
    if (s.includes(",") || s.includes("\"") || s.includes("\n")) {
      return `"${s.replace(/"/g, '""')}"`
    }
    return s
  }
  const csv = [
    headers.join(","),
    ...rows.map(r => r.map(escape).join(",")),
  ].join("\n")
  const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" })
  const url = URL.createObjectURL(blob)
  const link = document.createElement("a")
  link.href = url
  link.setAttribute("download", filename)
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}
